import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { brandText } from '../../../../config/branding';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import endConsumerV3Api from '../../../../utils/api/v2/end-consumer';

interface AddWalletProperties {
  currency: string;
  amount: number | null;
}

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Component to add money to a user's wallet
 * @param {any} param0 properties - The properties object
 * @param {any} param0.closeModal - The closeModal prop
 * @param {any} param0.endConsumerId - The endConsumerId prop
 * @returns {any} - The AddWallet component
 */
function AddWallet({
  closeModal,
  endConsumerId,
}: {
  closeModal: () => void;
  endConsumerId: string;
}) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('endConsumer');
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [wallet, setWallet] = useState<AddWalletProperties>({ currency: 'SAR', amount: null });
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  /**
   * Handles input changes for the wallet amount and currency
   * @param {string} name - The input name
   * @param {any} value - The input value
   */
  const handleInputChange = (name: string, value: string | number) => {
    setWallet((previous) => ({ ...previous, [name]: value }));
  };

  /**
   * Handles the submission of the add wallet form, making an API call to add money to the user's wallet
   */
  const handleSubmit = async () => {
    const validatedEndConsumerId = endConsumerId?.trim();
    if (!validatedEndConsumerId) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('unableToAddWalletFundsWithoutAValidEndConsumerID'),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }
    setLoading(true);
    try {
      const response = await endConsumerV3Api.topUpWallet(validatedEndConsumerId, {
        amount: wallet.amount,
        currency: wallet.currency,
      });

      if (response?.status >= 200 && response?.status < 300) {
        setOpenStatus(true);
        setModalView({
          title: '',
          content: (
            <SuccessNotification
              title={t('amountAddedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setOpenStatus(false);
                closeModal();
              }}
            />
          ),
          header: false,
          rightCorner: '',
          notification: true,
        });
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('somethingWentWrong'),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-money" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        closeModal={() => setOpenStatus(false)}
        notification={modalView.notification}
      />
      <div className="add-money__inputs">
        <CustomInput
          name="amount"
          type="number"
          value={wallet.amount}
          placeholder={t('enterAnAmount')}
          onChangeValue={handleInputChange}
          maxValue={10000}
          decimalLimit={2}
        />
        <SelectField
          name="currency"
          onChangeValue={(name, value) =>
            setWallet((previous) => ({ ...previous, currency: value }))
          }
          options={brandText.currency.split(',').map((option) => ({
            label: option.trim(),
            value: option.trim(),
          }))}
          selectedValue={wallet.currency}
        />
      </div>
      <div className="add-money__action">
        <MyButton
          label={t('cancel')}
          buttonType="error"
          onClickFunc={closeModal}
          disableButton={loading}
        />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={wallet.amount !== null ? handleSubmit : undefined}
          disableButton={loading || wallet.amount === null}
          loading={loading}
        />
      </div>
    </div>
  );
}

export default AddWallet;
